<template>
  <div
    v-if="loadingDetail"
    class="eu-pa_6 eu-align_center"
  >
    <emd-loader :type="'_invert'"></emd-loader>
  </div>
  <div v-else>
    <div class="el-section">
      <div class="el-grid-list">
        <div class="emd-container">
          <div
            class="emd-status-bar"
            v-bind:class="statusClass(plan.status)"
          ></div>
          <div class="emd-container__content">
            <div class="el-chunk">
              <h4 class="emd-text _color_subtle _type_label _size_tiny">
                {{ $t('plan.info.status') }}
              </h4>
            </div>
            <span
              class="emd-badge"
              v-bind:class="statusClass(plan.status)"
              >{{ plan.status }}</span
            >
          </div>
        </div>
        <div class="emd-container">
          <div class="emd-container__content">
            <div class="el-chunk">
              <h4 class="emd-text _color_subtle _type_label _size_tiny">
                {{ $t('plan.info.billing-type') }}
              </h4>
            </div>
            <p class="emd-text _size_section">{{ plan.billing_type }}</p>
          </div>
        </div>
        <div class="emd-container">
          <div class="emd-container__content">
            <div class="el-chunk">
              <h4 class="emd-text _color_subtle _type_label _size_tiny">
                {{ $t('plan.info.interval') }}
              </h4>
            </div>
            <p class="emd-text _size_section">
              {{ $t('plan.info.interval-each') }} {{ plan.interval_count }}
              {{ pluralIntervals(plan.interval_count, plan.interval) }}
            </p>
          </div>
        </div>
        <div class="emd-container">
          <div class="emd-container__content">
            <div class="el-chunk">
              <h4 class="emd-text _color_subtle _type_label _size_tiny">
                {{ $t('plan.info.created-at') }}
              </h4>
            </div>
            <p class="emd-text _size_section">
              {{ plan.created_at | dateFormat('date') }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="el-section">
      <div class="emd-container _highlight_header">
        <div class="emd-container__header">
          <h2 class="emd-text _size_section">{{ $t('plan.info.info') }}</h2>
        </div>
        <div class="emd-container__content">
          <div class="emd-panel">
            <div class="emd-panel__section">
              <div class="emd-panel__block">
                <div class="el-chunk">
                  <h2 class="emd-text _color_default _type_caption">
                    {{ $t('plan.info.general-data') }}
                  </h2>
                </div>
              </div>
              <div class="emd-panel__block">
                <div class="el-chunk">
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    ID
                  </p>
                  <span>{{ plan.id || '-' }}</span>
                </div>
                <div class="el-chunk">
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('plan.info.name') }}
                  </p>
                  <span>{{ plan.name || '-' }}</span>
                </div>
                <div class="el-chunk">
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('plan.info.description') }}
                  </p>
                  <span>{{ plan.description || '-' }}</span>
                </div>
                <div class="el-chunk">
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('plan.info.currency') }}
                  </p>
                  <span>{{ plan.currency || '-' }}</span>
                </div>
                <div class="el-chunk">
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('plan.info.payment-method') }}
                  </p>
                  <span
                    v-for="(payment_method, index) in plan.payment_methods"
                    :key="index"
                  >
                    {{ payment_method || '-' }}
                    <span v-if="index !== plan.payment_methods.length - 1">
                      |
                    </span>
                  </span>
                </div>
                <div
                  class="el-chunk"
                  v-if="plan.minimum_price"
                >
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('plan.info.minimum-price') }}
                  </p>
                  <span>{{ plan.minimum_price || '-' }}</span>
                </div>
                <div
                  class="el-chunk"
                  v-if="plan.trial_period_days"
                >
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('plan.info.trial') }}
                  </p>
                  <span
                    >{{ plan.trial_period_days }}
                    <span v-if="plan.trial_period_days <= 1">{{
                      $t('plan.info.trial-day')
                    }}</span>
                    <span v-if="plan.trial_period_days > 1">{{
                      $t('plan.info.trial-days')
                    }}</span>
                  </span>
                </div>
                <div
                  class="el-chunk"
                  v-if="plan.updated_at"
                >
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('plan.info.updated-at') }}
                  </p>
                  <span>{{ plan.updated_at | dateFormat('dateAndHour') }}</span>
                </div>
                <div
                  class="el-chunk"
                  v-if="plan.metadata && Object.entries(plan.metadata).length"
                >
                  <p class="emd-text _color_subtle _type_label _size_tiny">
                    {{ $t('plan.info.more-info') }}
                  </p>
                  <pre>{{ plan.metadata }}</pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="el-section"
        v-if="plan.items.length"
      >
        <div class="emd-container _highlight_header">
          <div class="emd-container__header">
            <h2 class="emd-text _size_section">
              {{ $t('plan.info.items.title') }}
            </h2>
          </div>
          <div class="emd-table-responsive">
            <table class="emd-table">
              <thead>
                <tr>
                  <th>{{ $t('plan.info.items.name') }}</th>
                  <th>{{ $t('plan.info.items.status') }}</th>
                  <th>{{ $t('plan.info.items.quantity') }}</th>
                  <th>{{ $t('plan.info.items.scheme') }}</th>
                  <th>{{ $t('plan.info.items.amount') }}</th>
                  <th>{{ $t('plan.info.items.updated-at') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, index) in plan.items"
                  :key="index"
                >
                  <td :data-label="$t('plan.info.items.name')">
                    {{ item.name || '-' }}
                  </td>
                  <td :data-label="$t('plan.info.items.status')">
                    {{ item.status || '-' }}
                  </td>
                  <td :data-label="$t('plan.info.items.quantity')">
                    {{ item.quantity || '-' }}
                  </td>
                  <td :data-label="$t('plan.info.items.scheme')">
                    {{ item.pricing_scheme.scheme_type || '-' }}
                  </td>
                  <td :data-label="$t('plan.info.items.amount')">
                    <div v-if="item.pricing_scheme.price_brackets.length">
                      <p
                        class="eu-pb_2"
                        v-for="(price, index) in item.pricing_scheme
                          .price_brackets"
                        :key="index"
                      >
                        {{ $t('plan.info.items.interval-from') }}
                        {{ price.start_quantity }}
                        {{ $t('plan.info.items.interval-to') }}
                        {{ price.end_quantity }}
                        <i class="eu-mr_1 eu-ml_1 uil uil-arrow-right" />
                        <span class="emd-text _type_caption">{{
                          price.price
                        }}</span>
                      </p>
                      <p class="eu-pb_2">
                        {{ $t('plan.info.items.interval-over') }}
                        {{
                          lastPrice(item.pricing_scheme.price_brackets)
                            .end_quantity
                        }}
                        <i class="eu-mr_1 eu-ml_1 uil uil-arrow-right" />
                        <span class="emd-text _type_caption">{{
                          lastPrice(item.pricing_scheme.price_brackets)
                            .overage_price
                        }}</span>
                      </p>
                    </div>
                    <div v-else>
                      {{ item.pricing_scheme.price || '-' }}
                    </div>
                  </td>
                  <td :data-label="$t('plan.info.items.updated-at')">
                    {{ item.updated_at | dateFormat('dateAndHour') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <log-links
      :entity="plan"
      :title="$t('log-links.plan')"
    ></log-links>
  </div>
</template>
<script>
import { statusClass, pluralIntervals } from '@/filters'
import { EmdLoader } from 'emerald-vue/src/components'
import LogLinks from '@/components/project/molecules/LogLinks/index.vue'
export default {
  name: 'PlanInfo',
  components: {
    EmdLoader,
    LogLinks
  },
  data () {
    return {
      statusClass: statusClass,
      pluralIntervals: pluralIntervals,
      plan: null,
      loadingDetail: true,
      params: {}
    }
  },
  methods: {
    lastPrice (items) {
      return items[items.length - 1]
    }
  },
  computed: {
    merchant () {
      return this.$store.getters.merchant
    },
    account () {
      return this.$store.getters.account
    },
    isTestAccount () {
      return this.account.account_type.value === 'test'
    }
  },
  beforeMount () {
    if (this.$route.params.tab === 'info') {
      this.params = {
        merchantId: this.merchant.id,
        accountId: this.account.id,
        planId: this.$route.params.id
      }
      this.params.query = {
        is_test_account: this.isTestAccount,
        created_at: this.$route.query.created_at
      }
      this.$store.dispatch('GET_PLAN', this.params).then(res => {
        this.plan = res
        this.loadingDetail = false
      })
    }
  }
}
</script>
